import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { HERO } from "@/constants/testIds";
import { imgUrl, bySlug } from "@/data/products";
import { Search, SlidersHorizontal, Download, Store, Compass, Hammer, Coins } from "lucide-react";

const PREVIEW = ["emerald-dragon", "cottage-house", "cosmic-helmet", "warlord-axe"];
const CHIPS = ["Figurines", "Home Decor", "Tools", "Cosplay"];

const HIGHLIGHTS = [
  { icon: Compass, label: "Discover designs" },
  { icon: Hammer, label: "Made by makers" },
  { icon: Coins, label: "Sell & earn" },
];

export const Hero = ({ onScrollTo }) => {
  return (
    <section id="top" className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px circle at 80% 28%, rgba(0,229,255,0.10), transparent 55%), radial-gradient(700px circle at 12% 12%, rgba(212,0,255,0.08), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 bg-grid opacity-40" aria-hidden />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:py-24 xl:py-28">
        {/* Left: copy */}
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-neo-cyan">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--neo-cyan)]" />
              Now launching &bull; Print it. Sell it. Love it.
            </span>
          </Reveal>

          <Reveal delay={0.05}>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              A whole new
              <br />
              <span className="gradient-text">3D printing</span> marketplace.
            </h1>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Meet Printopia &mdash; the marketplace where you discover
              one-of-a-kind prints from independent makers and get them shipped
              to your door. Have a printer? List your designs and turn it into a
              money-making side gig.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid={HERO.primaryCta}
                onClick={() => onScrollTo?.("download")}
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] px-5 text-base font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color,transform] duration-200 hover:bg-[rgba(0,229,255,0.24)] active:scale-[0.98]"
              >
                <Download className="h-4 w-4" /> Download the App
              </button>
              <button
                data-testid={HERO.waitlistCta}
                onClick={() => onScrollTo?.("download")}
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] border border-border/70 bg-secondary/60 px-5 text-base font-medium text-foreground transition-[border-color,box-shadow] duration-200 hover:border-[rgba(212,0,255,0.4)] hover:shadow-[var(--glow-magenta)]"
              >
                <Store className="h-4 w-4" /> Sell your designs
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-6">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label} className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/70 bg-secondary/50">
                    <h.icon className="h-4 w-4 text-neo-cyan" />
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {h.label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right: marketplace preview card */}
        <Reveal delay={0.1} y={0} x={24} className="relative">
          <div className="relative mx-auto max-w-md">
            <div className="relative z-10 overflow-hidden rounded-3xl border border-border/70 bg-card/80 p-5 shadow-[var(--shadow-elev-2)] backdrop-blur-md">
              <div className="flex items-center gap-2">
                <div className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-border/70 bg-background/50 px-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-xs text-muted-foreground">
                    Search 3D designs&hellip;
                  </span>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/70 bg-background/50">
                  <SlidersHorizontal className="h-4 w-4 text-neo-cyan" />
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {CHIPS.map((c, i) => (
                  <span
                    key={c}
                    className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${
                      i === 0
                        ? "border-[rgba(0,229,255,0.5)] bg-[rgba(0,229,255,0.12)] text-neo-cyan"
                        : "border-border/70 bg-secondary/40 text-muted-foreground"
                    }`}
                  >
                    {c}
                  </span>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                {PREVIEW.map((slug, i) => {
                  const p = bySlug(slug);
                  return (
                    <motion.div
                      key={slug}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                      className="overflow-hidden rounded-2xl border border-border/70"
                    >
                      <div
                        className="relative h-28"
                        style={{ background: "radial-gradient(circle at 50% 35%, #f6f8fc, #dfe5ee)" }}
                      >
                        <img src={imgUrl(slug)} alt={p?.name} className="h-full w-full object-contain p-2" />
                        <span className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 font-mono text-[10px] font-semibold text-white">
                          ${p?.price}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2">
                        <span className="truncate font-mono text-[10px] text-foreground">
                          {p?.name}
                        </span>
                        <span className="font-mono text-[10px] text-neo-cyan">View</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <button
                onClick={() => onScrollTo?.("download")}
                className="mt-4 w-full rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] py-3 text-sm font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color] duration-200 hover:bg-[rgba(0,229,255,0.24)]"
              >
                Get the app to shop
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
